import { useEffect, useMemo, useState } from 'react';
import { listRenterFailedFiles } from 'src/services/aiFileUploadV2';
import type { RenterFileListItem } from 'src/types/aiFileUploadV2';

/**
 * Merge session-scoped failure entries (from the live upload state) with
 * failures persisted server-side, de-duplicating by file id. Session entries
 * win because they carry the freshest WS-patched data.
 */
export function mergeFailedFiles(
  sessionFailed: Record<number, RenterFileListItem> | undefined,
  persistedFailed: RenterFileListItem[]
): RenterFileListItem[] {
  const seen = new Set<number>();
  const merged: RenterFileListItem[] = [];
  for (const f of [...Object.values(sessionFailed ?? {}), ...persistedFailed]) {
    if (!seen.has(f.id)) {
      seen.add(f.id);
      merged.push(f);
    }
  }
  return merged;
}

/**
 * Restrict persisted failed files to the file question (QIT) they were
 * uploaded to. The failed-files endpoint is survey-scoped, so without this
 * every interview question's upload input would surface failures belonging to
 * any other question in the survey. A null/undefined `qit` means "no QIT
 * scoping" (the standalone upload page covers the whole survey/certification).
 */
export function scopeFailedFilesToQit(
  files: RenterFileListItem[],
  qit?: number | null
): RenterFileListItem[] {
  if (qit == null) {
    return files;
  }
  return files.filter((f) => f.target_qit_id === qit);
}

interface UseMergedFailedFilesParams {
  survey?: number | null;
  certification?: number | null;
  /** File question the upload input is anchored to (interview embed). */
  qit?: number | null;
}

/**
 * Loads failed/rejected files the server already knows about and merges them
 * with the session's `failedEntries` for inline error banners (ENG-3265).
 *
 * Shared by the renter upload page and the in-interview upload input so the
 * two surfaces stay in sync. When `qit` is provided (interview embed) only
 * failures targeting that question are surfaced.
 */
export function useMergedFailedFiles(
  { survey, certification, qit }: UseMergedFailedFilesParams,
  sessionFailed: Record<number, RenterFileListItem> | undefined
): RenterFileListItem[] {
  const [persistedFailed, setPersistedFailed] = useState<RenterFileListItem[]>(
    []
  );

  useEffect(() => {
    if (survey == null && certification == null) {
      return;
    }
    let cancelled = false;
    listRenterFailedFiles({
      survey: survey ?? undefined,
      certification: certification ?? undefined,
    })
      .then((files) => {
        if (!cancelled) {
          setPersistedFailed(scopeFailedFilesToQit(files, qit));
        }
      })
      .catch(() => {
        // non-fatal — session error banners still show
      });
    return () => {
      cancelled = true;
    };
  }, [survey, certification, qit]);

  return useMemo(
    () => mergeFailedFiles(sessionFailed, persistedFailed),
    [sessionFailed, persistedFailed]
  );
}
